/*
 * *****************************************************************************
 *     Copyright (C)  Motorola Solutions, INC.
 *     All Rights Reserved.
 *     Motorola Solutions Confidential Restricted.
 * *****************************************************************************
 */

import { Injectable } from '@angular/core';
import { IEnvironment } from '../../../assets/config/environment';
import { Meta } from '@angular/platform-browser';
import { InitializationService } from './initialization.service';
import { defer, firstValueFrom, from, retry } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/**
 * Inject this service to use environment variables
 * constructor(private env: EnvironmentService) {}
 */
@Injectable({
    providedIn: 'root'
})
export class EnvironmentService {
    public static readonly CONFIG_URL = 'assets/config/config.json';
    private _env!: IEnvironment;
    private _environment!: Promise<IEnvironment>;

    constructor(
        private meta: Meta,
        private initializationService: InitializationService
    ) {}

    get environment(): IEnvironment {
        return this._env;
    }

    get serviceUrls(): string[] {
        if (this.environment) {
            const arr = Object.values(this.environment.SERVICES).flat().map((s) => s.url);
            return this.environment.CTC_URL ? arr.concat(this.environment.CTC_URL) : arr;
        }
        return [];
    }

    /**
     * Check if NRRC (New Reason/Result Code) feature is globally enabled.
     * This is the primary feature flag that must be true for any NRRC behavior.
     * @returns true if environment.featureFlags.nrrcEnabled is explicitly true
     */
    isNrrcGloballyEnabled(): boolean {
        return this.environment?.featureFlags?.nrrcEnabled === true;
    }

    /**
     * Load configuration from /assets/config/config.json
     * during app initialization
     */
    loadConfiguration(): Promise<IEnvironment> {
        this._environment = this._environment
            ? this._environment
            : firstValueFrom(
                defer(() => from(fetch(EnvironmentService.CONFIG_URL).then((response) => response.json()))).pipe(
                    catchError((err) => {
                        this.initializationService.setInfo('Failure to load client configuration. Retrying...');
                        throw err;
                    }),
                    retry({ delay: 3000 }),
                    tap((env) => {
                        this._env = env;
                        if (env.CSP) {
                            this.meta.addTag({
                                'http-equiv': 'Content-Security-Policy',
                                content: env.CSP
                            });
                        }
                    })
                )
            );
        return this._environment;
    }
}
